import React, { useState } from 'react';
import { Card, List, Button, Typography, Space, InputNumber, Popconfirm, Empty, Divider, App } from 'antd';
import { DeleteOutlined, ShoppingCartOutlined, MinusOutlined, PlusOutlined, StopOutlined, EditOutlined } from '@ant-design/icons';
import { Order, OrderItem, CancelOrderItemRequest } from '../../types/orders';
import { ProductOrderItem } from '../../types/products';
import { useCancelOrderItemsMutation } from '../../services/ordersApi';
import { CancellationModal } from './modals/CancellationModal';
import { useAppSelector } from '../../hooks/redux';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import SeatPill from '../common/SeatPill';

const { Text } = Typography;

interface OrderSummaryProps {
  order: Order | null;
  onUpdateQuantity?: (itemId: string, quantity: number) => void;
  onRemoveItem?: (itemId: string) => void;
  onCloseOrder?: () => void;
  loading?: boolean;
  // New props for handling new items
  newItems?: ProductOrderItem[];
  onRemoveNewItem?: (index: number) => void;
  onUpdateNewItemQuantity?: (index: number, quantity: number) => void;
  onEditNewItem?: (index: number) => void;
  showActions?: boolean;
  showExistingItems?: boolean;
  // Cuentas por comensal: muestra la etiqueta C1/C2 en los renglones nuevos y
  // deja tocarla para pasar el renglón al siguiente comensal.
  showSeats?: boolean;
  onCycleNewItemSeat?: (index: number) => void;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  order,
  onUpdateQuantity,
  onRemoveItem,
  onCloseOrder,
  loading = false,
  newItems = [],
  onRemoveNewItem,
  onUpdateNewItemQuantity,
  onEditNewItem,
  showActions = true,
  showExistingItems = true,
  showSeats = false,
  onCycleNewItemSeat,
}) => {
  const { message } = App.useApp();
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [cancelOrderItems, { isLoading: isCancelling }] = useCancelOrderItemsMutation();
  const { user } = useAppSelector(state => state.auth);
  const { formatInTimeZone } = useRestaurantTimeZone();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price).replace('$', '$');
  };

  const handleCancellation = async (cancellations: CancelOrderItemRequest[]) => {
    if (!order) return;

    try {
      // Convert to the format expected by the bulk cancellation API
      const cancelData = {
        itemsToCancel: cancellations,
        generalReason: cancellations[0]?.Reason || '',
        generalNotes: cancellations[0]?.Notes,
        AuthorizedByUserId: cancellations[0]?.AuthorizedByUserId || ''
      };

      await cancelOrderItems({
        orderId: order.id,
        ...cancelData
      }).unwrap();

      message.success(`${cancellations.length} item(s) cancelled successfully`);
      setShowCancellationModal(false);
    } catch (error: any) {
      console.error('Error cancelling items:', error);
      message.error(error?.data?.message || 'Error cancelling items');
    }
  };

  if (!order) {
    return (
      <Card
        title={
          <Space>
            <ShoppingCartOutlined />
            <span>Orden Actual</span>
          </Space>
        }
        style={{ height: '100%' }}
      >
        <Empty
          image={<ShoppingCartOutlined style={{ fontSize: 48, color: 'var(--border-color-strong)' }} />}
          description="No hay orden activa"
        />
      </Card>
    );
  }

  return (
    <Card
      style={{ height: '100%' }}
      styles={{ body: { padding: '12px' } }}
    >
      {/* Existing Order Items */}
      {order.orderItems.length === 0 && newItems.length === 0 ? (
        <Empty
          image={<ShoppingCartOutlined style={{ fontSize: 48, color: 'var(--border-color-strong)' }} />}
          description="Orden vacía"
          style={{ margin: '20px 0' }}
        />
      ) : (
        <>
          {/* New Items Section */}
          {newItems.length > 0 && (
            <>
              <div>
                {newItems.map((item: ProductOrderItem, index: number) => (
                  <div
                    key={index}
                    style={{
                      padding: '10px 12px',
                      borderBottom: '1px solid var(--border-color)',
                      backgroundColor: 'var(--bg-success-light)',
                    }}
                  >
                    {/* Row 1: Product name + total price */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                      <Text strong style={{ fontSize: '14px', flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                        {showSeats && (
                          <span
                            role="button"
                            title="Cambiar comensal"
                            onClick={() => onCycleNewItemSeat?.(index)}
                            style={{ marginRight: 6, cursor: onCycleNewItemSeat ? 'pointer' : 'default' }}
                          >
                            <SeatPill seatNumber={item.seatNumber} showShared size="small" />
                          </span>
                        )}
                        {item.productName}
                      </Text>
                      <Text strong style={{ color: 'var(--color-success)', fontSize: '14px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                        {formatPrice(item.quantity * item.unitPrice)}
                      </Text>
                    </div>

                    {/* Row 2: Quantity controls + unit price (full width) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {item.dailyMenuId ? (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Cant: {item.quantity} x {formatPrice(item.unitPrice)}
                        </Text>
                      ) : (
                        <>
                          <Button
                            size="small"
                            icon={<MinusOutlined />}
                            onClick={() => onUpdateNewItemQuantity && onUpdateNewItemQuantity(index, Math.max(1, item.quantity - 1))}
                            disabled={item.quantity <= 1}
                          />
                          <InputNumber
                            size="small"
                            min={1}
                            max={99}
                            value={item.quantity}
                            onChange={(value) => onUpdateNewItemQuantity && onUpdateNewItemQuantity(index, value || 1)}
                            style={{ width: '50px', textAlign: 'center' }}
                          />
                          <Button
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => onUpdateNewItemQuantity && onUpdateNewItemQuantity(index, item.quantity + 1)}
                            disabled={item.quantity >= 99}
                          />
                          <Text type="secondary" style={{ fontSize: '11px', marginLeft: 4, whiteSpace: 'nowrap' }}>
                            x {formatPrice(item.unitPrice)}
                          </Text>
                        </>
                      )}
                    </div>

                    {/* Row 3: Variants */}
                    {item.dailyMenuSelections && item.dailyMenuSelections.length > 0 ? (
                      <div style={{ marginTop: 6 }}>
                        {item.dailyMenuSelections.map((sel) => (
                          <Text
                            key={sel.courseId}
                            type="secondary"
                            style={{ fontSize: '11px', display: 'block' }}
                          >
                            {sel.courseName}: {sel.optionName || ''}{sel.priceModifier > 0 ? ` (+${formatPrice(sel.priceModifier)})` : ''}
                          </Text>
                        ))}
                      </div>
                    ) : item.selectedVariants.length > 0 ? (
                      <div style={{ marginTop: 6 }}>
                        {item.selectedVariants.map((variant) => (
                          <Text
                            key={variant.id}
                            type="secondary"
                            style={{ fontSize: '11px', display: 'block' }}
                          >
                            + {variant.name} ({formatPrice(variant.priceModifier)})
                          </Text>
                        ))}
                      </div>
                    ) : null}

                    {/* Row 4: Notes */}
                    {item.notes && (
                      <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic', display: 'block', marginTop: 4 }}>
                        Nota: {item.notes}
                      </Text>
                    )}

                    {/* Row 5: Action buttons (edit/delete) */}
                    <div style={{
                      display: 'flex',
                      gap: 8,
                      marginTop: 8,
                      paddingTop: 6,
                      borderTop: '1px dashed var(--border-color)'
                    }}>
                      {onEditNewItem && (
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          size="small"
                          onClick={() => onEditNewItem(index)}
                          style={{ flex: 1 }}
                        >
                          Editar
                        </Button>
                      )}
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        size="small"
                        danger
                        onClick={() => onRemoveNewItem && onRemoveNewItem(index)}
                        style={{ flex: 1 }}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              {showExistingItems && order.orderItems.length > 0 && (
                <Divider style={{ margin: '12px 0' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Productos en la orden</Text>
                </Divider>
              )}
            </>
          )}

          {/* Existing Order Items */}
          {showExistingItems && order.orderItems.length > 0 && (
            <List
              dataSource={order.orderItems}
              renderItem={(item: OrderItem) => (
              <List.Item
                style={{ 
                  padding: '8px 0', 
                  borderBottom: '1px solid var(--border-color)',
                  opacity: item.isCancelled ? 0.5 : 1,
                  backgroundColor: item.isCancelled ? 'var(--bg-tertiary)' : 'transparent'
                }}
                actions={item.isCancelled ? [] : [
                  <Popconfirm
                    title="¿Eliminar este producto?"
                    onConfirm={() => onRemoveItem && onRemoveItem(item.id)}
                    okText="Sí"
                    cancelText="No"
                  >
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                    />
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <Text strong style={{ fontSize: '14px' }}>
                        {item.productName}
                        {item.isCancelled && (
                          <Text type="danger" style={{ fontSize: '12px', marginLeft: '8px' }}>
                            (CANCELADO)
                          </Text>
                        )}
                      </Text>
                      <Text strong style={{ color: 'var(--color-success)', fontSize: '14px' }}>
                        {formatPrice(item.total)}
                      </Text>
                    </div>
                  }
                  description={
                    <div>
                      {!item.isCancelled ? (
                        item.dailyMenuId ? (
                          <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px' }}>
                            Cant: {item.quantity} x {formatPrice(item.unitPrice)}
                          </Text>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                            <Button
                              size="small"
                              icon={<MinusOutlined />}
                              onClick={() => onUpdateQuantity && onUpdateQuantity(item.id, Math.max(1, item.quantity - 1))}
                              disabled={item.quantity <= 1}
                            />
                            <InputNumber
                              size="small"
                              min={1}
                              max={99}
                              value={item.quantity}
                              onChange={(value) => onUpdateQuantity && onUpdateQuantity(item.id, value || 1)}
                              style={{ width: '60px', textAlign: 'center' }}
                            />
                            <Button
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() => onUpdateQuantity && onUpdateQuantity(item.id, item.quantity + 1)}
                              disabled={item.quantity >= 99}
                            />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              x {formatPrice(item.unitPrice)}
                            </Text>
                          </div>
                        )
                      ) : (
                        <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px' }}>
                          Cantidad: {item.quantity} x {formatPrice(item.unitPrice)} (Cancelado)
                        </Text>
                      )}

                      {item.notes && (
                        <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                          Nota: {item.notes}
                        </Text>
                      )}

                      {item.dailyMenuSelections && item.dailyMenuSelections.length > 0 ? (
                        <div style={{ marginTop: '4px' }}>
                          {item.dailyMenuSelections.map((sel) => (
                            <Text
                              key={sel.id}
                              type="secondary"
                              style={{ fontSize: '11px', display: 'block' }}
                            >
                              {sel.courseName}: {sel.optionName}{sel.priceModifier > 0 ? ` (+${formatPrice(sel.priceModifier)})` : ''}
                            </Text>
                          ))}
                        </div>
                      ) : item.variants.length > 0 ? (
                        <div style={{ marginTop: '4px' }}>
                          {item.variants.map((variant) => (
                            <Text
                              key={variant.id}
                              type="secondary"
                              style={{ fontSize: '11px', display: 'block' }}
                            >
                              + {variant.variantName} ({formatPrice(variant.priceModifier)})
                            </Text>
                          ))}
                        </div>
                      ) : null}
                      {item.dispatchedAt && !item.isCancelled && (
                        <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>
                          Despachado: {formatInTimeZone(item.dispatchedAt, 'HH:mm')}
                        </Text>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
            />
          )}

          {showActions && (
            <Space direction="vertical" style={{ width: '100%' }}>
                {showExistingItems && order && order.orderItems.some(item => !item.isCancelled) && (
                <Button
                  type="default"
                  size="large"
                  icon={<StopOutlined />}
                  onClick={() => setShowCancellationModal(true)}
                  loading={isCancelling}
                  style={{ width: '100%' }}
                  danger
                >
                  Cancelar Productos
                </Button>
              )}
              
              {onCloseOrder && (
                <Button
                  type="primary"
                  size="large"
                  onClick={onCloseOrder}
                  loading={loading}
                  style={{ width: '100%' }}
                >
                  Cerrar Orden
                </Button>
              )}
            </Space>
          )}
        </>
      )}
      
      {/* Cancellation Modal */}
      {order && (
        <CancellationModal
          visible={showCancellationModal}
          orderItems={order.orderItems || []}
          onCancel={() => setShowCancellationModal(false)}
          onConfirm={handleCancellation}
          loading={isCancelling}
          currentUser={user || undefined}
        />
      )}
    </Card>
  );
};

export default OrderSummary;